import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Chart } from 'chart.js';
import { Theme, ThemeService } from '../../theme/theme.service';

@Component({
    selector: 'app-small-line-chart',
    templateUrl: './small-line-chart.component.html',
    styleUrls: ['./small-line-chart.component.scss']
})
export class SmallLineChartComponent implements OnInit, OnChanges {

    @Input() public title: string;
    @Input() public data: { x: number; y: number }[] = [];
    @Input() public xMin = 0;
    // eslint-disable-next-line no-magic-numbers
    @Input() public xMax = 16;
    @Input() public yMin = 0;

    public chart: Chart;

    @ViewChild('chart', {static: true, read: ElementRef}) private readonly element: ElementRef;

    constructor(private readonly themes: ThemeService) {}

    private darkThemeOptions() {
        return ({
            title: {
                display: this.title !== undefined,
                fontColor: 'white',
                text: this.title,
                fontSize: 6,
                position: 'bottom',
                padding: 1
            },
            responsive: true,
            maintainAspectRatio: true,
            legend: {
                display: false
            },
            tension: 0.4,
            elements: {
                point: {
                    radius: 0
                }
            },
            scales: {
                xAxes: [{
                    type: 'linear',
                    ticks: {
                        display: true,
                        min: this.xMin,
                        max: this.xMax,
                        // eslint-disable-next-line no-magic-numbers
                        stepSize: Math.floor((this.xMax - this.xMin) / 2),
                        fontColor: 'white',
                        fontSize: 6
                    },
                    gridLines: {
                        lineWidth: 0,
                        zeroLineWidth: 1,
                        zeroLineColor: 'white',
                        tickMarkLength: 2
                    }
                }],
                yAxes: [{
                    type: 'linear',
                    ticks: {
                        display: false,
                        min: this.yMin,
                        suggestedMax: 5
                    },
                    gridLines: {
                        drawBorder: true,
                        lineWidth: 0,
                        zeroLineWidth: 1,
                        zeroLineColor: 'white'
                    }
                }]
            }
        });
    }

    private lightThemeOptions() {
        return ({
            title: {
                display: this.title !== undefined,
                fontColor: 'black',
                text: this.title,
                fontSize: 6,
                position: 'bottom',
                padding: 1
            },
            responsive: true,
            maintainAspectRatio: true,
            legend: {
                display: false
            },
            tension: 0.4,
            elements: {
                point: {
                    radius: 0
                }
            },
            scales: {
                xAxes: [{
                    type: 'linear',
                    ticks: {
                        display: true,
                        min: this.xMin,
                        max: this.xMax,
                        // eslint-disable-next-line no-magic-numbers
                        stepSize: Math.floor((this.xMax - this.xMin) / 2),
                        fontColor: 'black',
                        fontSize: 6
                    },
                    gridLines: {
                        lineWidth: 0,
                        zeroLineWidth: 1,
                        zeroLineColor: 'black',
                        tickMarkLength: 2
                    }
                }],
                yAxes: [{
                    type: 'linear',
                    ticks: {
                        display: false,
                        min: this.yMin,
                        suggestedMax: 5
                    },
                    gridLines: {
                        drawBorder: true,
                        lineWidth: 0,
                        zeroLineWidth: 1,
                        zeroLineColor: 'black'
                    }
                }]
            }
        });
    }

    public ngOnInit(): void {
        this.chart = new Chart(this.element.nativeElement, {
            type: 'line',
            data: this.data,
            options: this.lightThemeOptions()
        });

        this.themes.themeChanges.subscribe(this.updateForTheme.bind(this));
    }

    private updateForTheme(theme: Theme) {
        if (theme === Theme.Light) {
            this.chart.options = this.lightThemeOptions();
            if (this.chart.data.datasets.length > 0) {
                this.chart.data.datasets[0].borderColor = 'black';
            }
        } else if (theme === Theme.Dark) {
            this.chart.options = this.darkThemeOptions();
            if (this.chart.data.datasets.length > 0) {
                this.chart.data.datasets[0].borderColor = 'white';
            }
        }
        this.chart.update();
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (this.chart && changes.hasOwnProperty('data')) {
            this.chart.data = {
                datasets: [
                    {
                        data: this.data,
                        fill: false,
                        borderColor: this.themes.theme() === Theme.Dark ? 'white' : 'black',
                        borderWidth: 1
                    }
                ]
            };
            this.chart.update();
        }
    }

}
